/**
 * Verifier Search Filters Component
 * Smart filters for finding the right verifier based on tier, skills, location, and task requirements
 */

import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
    Chip, Button, Grid, Autocomplete, Slider
} from '@mui/material';
import {
    FilterList, Clear, LocationOn, Build
} from '@mui/icons-material';

const DEFAULT_FILTERS = {
    verifierLevel: '',
    location: '',
    serviceAreas: [],
    skills: [],
    minRating: 0,
    minCompletionRate: 0,
    minExperience: 0,
    maxFeeRange: [0, 200],
    availability: 'any',
    verifiedOnly: false,
    insuranceRequired: false,
    milestoneType: '',
    projectValue: '',
    urgency: ''
};

const VerifierSearchFilters = ({ onFilterChange, initialFilters = {}, showTaskBasedFilters = false }) => {
    const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initialFilters });

    // Keep the panel in sync when the parent resets/replaces filters (e.g. Clear All).
    useEffect(() => {
        setFilters({ ...DEFAULT_FILTERS, ...initialFilters });
    }, [initialFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    // Zimbabwe cities
    const zimbabweCities = [
        'Harare', 'Bulawayo', 'Chitungwiza', 'Mutare', 'Gweru',
        'Kwekwe', 'Kadoma', 'Masvingo', 'Chinhoyi', 'Norton',
        'Marondera', 'Ruwa', 'Chegutu', 'Zvishavane', 'Bindura',
        'Beitbridge', 'Redcliff', 'Victoria Falls', 'Hwange', 'Chiredzi'
    ];

    // Skills by tier
    const generalSkills = [
        'Material Inspection', 'Photo Documentation', 'Quantity Verification',
        'Site Safety Assessment', 'Basic Measurement', 'Report Writing'
    ];

    const professionalSkills = [
        'Structural Analysis', 'Foundation Inspection', 'Building Code Compliance',
        'Technical Drawing Review', 'Quality Control', 'Material Testing',
        'Concrete Testing', 'Surveying', 'CAD Software', 'BIM'
    ];

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const handleMultiSelect = (key, value) => {
        const current = filters[key] || [];
        const updated = current.includes(value)
            ? current.filter(item => item !== value)
            : [...current, value];
        handleFilterChange(key, updated);
    };

    const handleClearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        onFilterChange(DEFAULT_FILTERS);
    };

    // Helper function to get verification count range based on level
    const getVerificationRange = (level) => {
        const ranges = {
            '1': { min: 0, max: 20 },
            '2': { min: 21, max: 50 },
            '3': { min: 51, max: 100 },
            '4': { min: 101, max: 200 },
            '5': { min: 201, max: 999999 }
        };
        return ranges[level] || null;
    };

    const getAvailableSkills = () => {
        // Show all skills available for selection
        return [...generalSkills, ...professionalSkills];
    };

    return (
        <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FilterList sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Advanced Filters</Typography>
                </Box>
                <Button
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    size="small"
                    color="secondary"
                >
                    Clear All
                </Button>
            </Box>

            <Grid container spacing={3}>
                {/* Verifier Level (based on verification count) */}
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Verifier Level</InputLabel>
                        <Select
                            value={filters.verifierLevel || ''}
                            onChange={(e) => handleFilterChange('verifierLevel', e.target.value)}
                            label="Verifier Level"
                        >
                            <MenuItem value="">All Levels</MenuItem>
                            <MenuItem value="1">Level 1 - Beginner (0-20 verifications)</MenuItem>
                            <MenuItem value="2">Level 2 - Intermediate (21-50 verifications)</MenuItem>
                            <MenuItem value="3">Level 3 - Advanced (51-100 verifications)</MenuItem>
                            <MenuItem value="4">Level 4 - Expert (101-200 verifications)</MenuItem>
                            <MenuItem value="5">Level 5 - Master (200+ verifications)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Location */}
                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        value={filters.location}
                        onChange={(e, newValue) => handleFilterChange('location', newValue || '')}
                        options={zimbabweCities}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="City / Location"
                                placeholder="Select city..."
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <LocationOn sx={{ mr: 0.5, color: 'action.active', fontSize: 20 }} />
                                            {params.InputProps.startAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                </Grid>

                {/* Minimum Rating - Dropdown with Slider */}
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Min Rating</InputLabel>
                        <Select
                            value={filters.minRating}
                            label="Min Rating"
                            renderValue={(value) => value === 0 ? 'Any Rating' : `${value.toFixed(1)}+ Stars`}
                        >
                            <Box sx={{ px: 3, py: 2, minWidth: 250 }} onClick={(e) => e.stopPropagation()}>
                                <Typography variant="body2" gutterBottom>
                                    Minimum Rating: {filters.minRating.toFixed(1)}+ Stars
                                </Typography>
                                <Slider
                                    value={filters.minRating}
                                    onChange={(e, value) => handleFilterChange('minRating', value)}
                                    min={0}
                                    max={5}
                                    step={0.5}
                                    marks={[
                                        { value: 0, label: '0' },
                                        { value: 2.5, label: '2.5' },
                                        { value: 5, label: '5★' }
                                    ]}
                                    valueLabelDisplay="auto"
                                />
                            </Box>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Minimum Experience - Dropdown with Slider */}
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Min Experience</InputLabel>
                        <Select
                            value={filters.minExperience}
                            label="Min Experience"
                            renderValue={(value) => value === 0 ? 'Any' : `${value}+ years`}
                        >
                            <Box sx={{ px: 3, py: 2, minWidth: 250 }} onClick={(e) => e.stopPropagation()}>
                                <Typography variant="body2" gutterBottom>
                                    Minimum Experience: {filters.minExperience}+ years
                                </Typography>
                                <Slider
                                    value={filters.minExperience}
                                    onChange={(e, value) => handleFilterChange('minExperience', value)}
                                    min={0}
                                    max={20}
                                    step={1}
                                    marks={[
                                        { value: 0, label: '0y' },
                                        { value: 10, label: '10y' },
                                        { value: 20, label: '20y' }
                                    ]}
                                    valueLabelDisplay="auto"
                                />
                            </Box>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Max Budget - Dropdown with Slider */}
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Max Budget</InputLabel>
                        <Select
                            value={filters.maxFeeRange[1]}
                            label="Max Budget"
                            renderValue={(value) => value === 200 ? 'Any Budget' : `Up to $${value}`}
                        >
                            <Box sx={{ px: 3, py: 2, minWidth: 250 }} onClick={(e) => e.stopPropagation()}>
                                <Typography variant="body2" gutterBottom>
                                    Maximum Budget: ${filters.maxFeeRange[1]}
                                </Typography>
                                <Slider
                                    value={filters.maxFeeRange[1]}
                                    onChange={(e, value) => handleFilterChange('maxFeeRange', [0, value])}
                                    min={0}
                                    max={200}
                                    step={10}
                                    marks={[
                                        { value: 0, label: '$0' },
                                        { value: 100, label: '$100' },
                                        { value: 200, label: '$200' }
                                    ]}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `$${value}`}
                                />
                            </Box>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Specializations/Skills - Multi-select Filter */}
                <Grid item xs={12} sm={6} md={6}>
                    <Autocomplete
                        multiple
                        value={filters.skills}
                        onChange={(e, newValue) => handleFilterChange('skills', newValue)}
                        options={getAvailableSkills()}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Specializations & Skills"
                                placeholder="Select skills..."
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option}
                                    {...getTagProps({ index })}
                                    size="small"
                                    icon={<Build />}
                                />
                            ))
                        }
                    />
                </Grid>
            </Grid>

            {/* Active Filters Summary */}
            {(filters.verifierLevel || filters.location || filters.skills.length > 0 || filters.minRating > 0 || filters.minExperience > 0) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        Active Filters:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {filters.verifierLevel && (
                            <Chip
                                label={`Level ${filters.verifierLevel}`}
                                size="small"
                                onDelete={() => handleFilterChange('verifierLevel', '')}
                                color="primary"
                            />
                        )}
                        {filters.location && (
                            <Chip
                                label={`Location: ${filters.location}`}
                                size="small"
                                onDelete={() => handleFilterChange('location', '')}
                                icon={<LocationOn />}
                            />
                        )}
                        {filters.minRating > 0 && (
                            <Chip
                                label={`${filters.minRating.toFixed(1)}+ Stars`}
                                size="small"
                                onDelete={() => handleFilterChange('minRating', 0)}
                            />
                        )}
                        {filters.minExperience > 0 && (
                            <Chip
                                label={`${filters.minExperience}+ years`}
                                size="small"
                                onDelete={() => handleFilterChange('minExperience', 0)}
                            />
                        )}
                        {filters.skills.map(skill => (
                            <Chip
                                key={skill}
                                label={skill}
                                size="small"
                                onDelete={() => handleMultiSelect('skills', skill)}
                                icon={<Build />}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default VerifierSearchFilters;

